import { IBookingTimeTable, IBookingTimeTableGroup, IBookingTimeTablePeriod } from 'models/shop/bookingtimetables';
import { IScheduleItemBase, ISubColumnedScheduleItemBase } from 'models/shop/schedule';
import { IServiceEditForm, IServicesList } from 'models/shop/shopBookingTemplates';
import moment = require('moment');
import { IStore } from 'redux/IStore';
import * as c from 'models/constants';
import * as i18n from 'i18next';
import { formatCurrency, formatDuration, uniq, readURLSearchParams, parseEntityId } from './common';
import { ShopStatus } from 'models';
import { IOfficeTime } from 'models/shop/office';
import { IServiceGroup } from 'models/shop/serviceGroups';
import { IShopServiceTemplate, IShopServiceTemplateItem, IShopServiceTreeItem } from 'models/shop/serviceTemplates';
import { groupServices } from 'redux/modules/shop/serviceGroups/helpers';
import { IServiceCategoryDomainValue } from 'models/common';
import { IOfficeMember } from 'models/shop/members';
import { momentWithTimezone } from 'redux/modules/shop/helpers';
import { groupBy } from 'containers/Shop/Helpers';

export const timeAsNumber = (m: moment.Moment): number => m.hours() + m.minutes() / 60;

/**
 * How many hours (float) m is after startTime
 * @param m the date
 * @param startTime absolute offset
 */
export const timeAsNumberRelative = (m: moment.Moment, startTime: moment.Moment): number =>
	(m.unix() - startTime.unix()) / (60 * 60);

export const timeFromNumber = (time: number, date: moment.Moment): moment.Moment =>
	date
		.clone()
		.hours(Math.floor(time))
		.minutes(Math.ceil(60 * (time % 1)));

// const formatTime = (t: string): string => moment(t).format('H:mm');
const formatTimeWithTimeZone = (t: string, timezone: string): string => momentWithTimezone(timezone, t).format('H:mm');

export const formatWorkTime = (time: IOfficeTime, timezone?: string): string =>
	formatTimeWithTimeZone(time.openTime, timezone) + ' - ' + formatTimeWithTimeZone(time.closeTime, timezone);

export const combineDateAndTime = (date: moment.Moment, time: moment.Moment, secondsToZero = false): moment.Moment =>
	date
		.clone()
		.hours(time.hours())
		.minutes(time.minutes())
		.seconds(secondsToZero ? 0 : time.seconds())
		.milliseconds(secondsToZero ? 0 : time.milliseconds());

type TimetableGroupsByResource = {
	available: { [resourceId: string]: IBookingTimeTableGroup[] };
	unavailable: { [resourceId: string]: IBookingTimeTableGroup[] };
	partial: { [resourceId: string]: IBookingTimeTableGroup[] };
};
type TimetablesByResource = {
	available: { [resourceId: string]: IBookingTimeTable[] };
	unavailable: { [resourceId: string]: IBookingTimeTable[] };
	partial: { [resourceId: string]: IBookingTimeTable[] };
};

export function normalizeBookingTimetablesMultipleResources(
	bookingTimetables: IBookingTimeTable[],
	timezone?: string,
): TimetableGroupsByResource {
	const timetablesByResource: TimetablesByResource = { available: {}, unavailable: {}, partial: {} };
	const timetableGroupsByResource: TimetableGroupsByResource = { available: {}, unavailable: {}, partial: {} };
	// const getType = (tt: IBookingTimeTable): keyof TimetableGroupsByResource =>
	//   parseEntityId(tt.entityId)?.type === "BookingTimeTable" ? "available" : "unavailable"
	// ;
	for (const tt of bookingTimetables) {
		const type: keyof TimetableGroupsByResource =
			parseEntityId(tt.entityId)?.type === 'BookingTimeTable'
				? 'available'
				: parseEntityId(tt.entityId)?.type === 'PartiallyUnavailableTimeTable'
				  ? 'partial'
				  : 'unavailable';

		if (!(tt['resourceId'] in timetablesByResource[type])) {
			timetablesByResource[type][tt['resourceId']] = [];
		}

		timetablesByResource[type][tt['resourceId']].push(tt);
	}

	for (const type in timetablesByResource) {
		for (const resourceId in timetablesByResource[type]) {
			timetableGroupsByResource[type][resourceId] = normalizeBookingTimetablesSingleResource(
				timetablesByResource[type][resourceId],
				timezone,
			);
		}
	}

	return timetableGroupsByResource;
}

export function normalizeBookingTimetablesSingleResource<
	T extends IBookingTimeTable | IBookingTimeTablePeriod = IBookingTimeTable,
>(bookingTimetables: T[], timezone: string): IBookingTimeTableGroup<T>[] {
	const makeDayIdentifier = (tt: T, i): { i: number; day: number } => ({
		i,
		day:
			(momentWithTimezone(timezone, tt.startTime).year() - 2020) * 366 +
			momentWithTimezone(timezone, tt.startTime).dayOfYear(),
	});

	const dayIdentifiers = groupBy(bookingTimetables.map(makeDayIdentifier), 'day');

	const groups: IBookingTimeTableGroup<T>[] = [];

	Object.keys(dayIdentifiers).map((dayIdentifier) => {
		const daily = dayIdentifiers[dayIdentifier].map((di) => bookingTimetables[di.i]);

		const dailyAsScheduleItems = daily.map((tt): T & IScheduleItemBase => ({
			...tt,
			start: timeAsNumber(momentWithTimezone(timezone, tt.startTime)),
			end: timeAsNumber(momentWithTimezone(timezone, tt.endTime)),
		}));

		// find all daily gaps (when there are no timetables, for example lunch time 12:00 - 15:00)
		let dayGaps = sortScheduleItems(splitTimeItem({ start: 0, end: 24 }, dailyAsScheduleItems, true, timezone));
		dayGaps = [...dayGaps, { start: 24, end: 25 }]; //fake gap in case schedule ends at midnight
		let lastEnd = 0;
		for (const gap of dayGaps) {
			if (gap.start > lastEnd) {
				const groupItems: T[] = dailyAsScheduleItems.filter(
					(dassi) => dassi.start >= lastEnd && dassi.start < gap.start,
				);
				if (!groupItems.length) {
					console.error('This is impossible');
					continue;
				}
				const minStartTime = groupItems.reduce(
					(acc, val) => (acc ? (acc > val.startTime ? val.startTime : acc) : val.startTime),
					'',
				);
				const maxEndTime = groupItems.reduce(
					(acc, val) => (acc ? (acc < val.endTime ? val.endTime : acc) : val.endTime),
					'',
				);
				const protoItem: T = {
					...groupItems[0],
					startTime: minStartTime,
					endTime: maxEndTime,
				};
				const t: IBookingTimeTableGroup<T> = {
					...protoItem,
					bookingTimetables: groupItems,
				};
				groups.push(t);
			}
			lastEnd = gap.end;
		}
	});

	return groups;
}

/**
 * filter resource time tables function by start and end times
 * @param bookingTimetables
 * @returns unique resource time tables by start and end time
 */
export function filterBookingTimetablesByUniqueStartAndEndTimes(
	bookingTimetables: IBookingTimeTable[],
	timezone?: string,
) {
	const uniqueTimetables = new Set();

	const filteredTimetables = bookingTimetables.filter((timetable) => {
		const startTime = momentWithTimezone(timezone, timetable.startTime);
		const endTime = momentWithTimezone(timezone, timetable.endTime);
		const timeObject = { startTime, endTime };

		if (!uniqueTimetables.has(JSON.stringify(timeObject))) {
			uniqueTimetables.add(JSON.stringify(timeObject));
			return true;
		}

		return false;
	});

	return filteredTimetables;
}

interface ITimeConflict {
	start: number;
	end: number;
	startTimeMoment?: moment.Moment;
	endTimeMoment?: moment.Moment;
	index: number;
	existingConflictedItem: ISubColumnedScheduleItemBase;
}

const findFirstTimeConflict = (
	existing: ISubColumnedScheduleItemBase[],
	injected: ISubColumnedScheduleItemBase[],
	timezone: string,
): ITimeConflict | null => {
	for (let index = 0; index < injected.length; index++) {
		const item = injected[index];
		for (const ex of existing) {
			const exStart = Math.max(ex.start, item.start);
			const exEnd = Math.min(ex.end, item.end);
			const exStartMoment = exStart == ex.start ? ex.startTimeMoment : item.startTimeMoment;
			const exEndMoment = exEnd == ex.end ? ex.endTimeMoment : item.endTimeMoment;
			if (exStart >= exEnd) {
				continue;
			}
			if (!doItemsOverlapColumns(item, ex)) {
				continue;
			}
			if (exStart >= item.start && exEnd <= item.end) {
				return {
					index,
					start: exStart,
					end: exEnd,
					startTimeMoment: momentWithTimezone(timezone, exStartMoment),
					endTimeMoment: momentWithTimezone(timezone, exEndMoment),
					existingConflictedItem: ex,
				};
			}
		}
	}
	return null;
};

export const doItemsOverlapColumns = (item1: ISubColumnedScheduleItemBase, item2: ISubColumnedScheduleItemBase) => {
	if (item1?.subColumn == null || item2?.subColumn == null) {
		return true;
	}
	return (
		item1.subColumn <= item2.subColumn + item2.subColSpan - 1 &&
		item1.subColumn + item1.subColSpan - 1 >= item2.subColumn
	);
};

export const isSubcolumned = (item) => {
	return item?.subColumn != null;
};

export function sortScheduleItems<TItem extends IScheduleItemBase>(items: TItem[]): TItem[] {
	return items.sort((a, b) => {
		if (a.start === b.start) {
			return a.end < b.end ? -1 : 1;
		}
		return a.start < b.start ? -1 : 1;
	});
}
/**
 * Split time item into multiple items, so they dont overlap with items from existing
 * @param item
 * @param existing
 * @param allowZeroWidthEvents allow to split items into sections that have the same start time as end time?
 * @returns
 */
export function splitTimeItem<TItem extends ISubColumnedScheduleItemBase>(
	item: TItem,
	existing: IScheduleItemBase[],
	allowZeroWidthEvents = false,
	timezone: string,
): TItem[] {
	const itemParts = [item];
	let conflict: ITimeConflict | null;
	while ((conflict = findFirstTimeConflict(existing, itemParts, timezone))) {
		const conflictedItem = itemParts.splice(conflict.index, 1)[0];
		const beforeConflict =
			conflictedItem.start < conflict.start || (allowZeroWidthEvents && conflictedItem.start == conflict.start)
				? { ...conflictedItem, end: conflict.start, endTimeMoment: conflict.startTimeMoment }
				: null;
		const afterConflict =
			conflictedItem.end > conflict.end || (allowZeroWidthEvents && conflictedItem.end == conflict.end)
				? { ...conflictedItem, start: conflict.end, startTimeMoment: conflict.endTimeMoment }
				: null;
		if (beforeConflict) {
			itemParts.push(beforeConflict);
		}
		if (afterConflict) {
			itemParts.push(afterConflict);
		}

		if (isSubcolumned(conflict.existingConflictedItem)) {
			const subColumnCount = conflict.existingConflictedItem.totalSubColumns;
			// If there is only one subcolumn, we can not have free space in the neighbouring subcolumns (they don't exist)
			if (subColumnCount > 1) {
				const columnPartStart = beforeConflict?.end || conflictedItem.start;
				const columnPartEnd = afterConflict?.start || conflictedItem.end;
				const columnPartStartMoment = beforeConflict?.endTimeMoment || conflictedItem.startTimeMoment;
				const columnPartEndMoment = afterConflict?.startTimeMoment || conflictedItem.endTimeMoment;
				const newItemPart = {
					...conflictedItem,
					start: columnPartStart,
					end: columnPartEnd,
					startTimeMoment: columnPartStartMoment,
					endTimeMoment: columnPartEndMoment,
					totalSubColumns: subColumnCount,
				};
				const beforeCol = [conflictedItem.subColumn || 0, conflict.existingConflictedItem.subColumn - 1];
				const afterCol = [
					conflict.existingConflictedItem.subColumn + conflict.existingConflictedItem.subColSpan,
					conflictedItem.subColumn + conflictedItem.subColSpan - 1 ||
						conflict.existingConflictedItem.totalSubColumns - 1,
				];
				if (beforeCol[0] <= beforeCol[1]) {
					itemParts.push({ ...newItemPart, subColumn: beforeCol[0], subColSpan: beforeCol[1] - beforeCol[0] + 1 });
				}
				if (afterCol[0] <= afterCol[1]) {
					itemParts.push({ ...newItemPart, subColumn: afterCol[0], subColSpan: afterCol[1] - afterCol[0] + 1 });
				}
			}
		}

		// Join together adjecent items (time based) in the same column
		itemParts.sort((a, b) =>
			a?.subColumn != null && b?.subColumn != null && a.subColumn != b.subColumn
				? a.subColumn - b.subColumn
				: a.start - b.start,
		);
		for (let i = 0; i < itemParts.length - 1; i++) {
			if (
				itemParts[i].subColumn != null &&
				itemParts[i + 1]?.subColumn != null &&
				itemParts[i]?.subColumn == itemParts[i + 1]?.subColumn &&
				itemParts[i].totalSubColumns == itemParts[i + 1].totalSubColumns &&
				itemParts[i].subColSpan == itemParts[i + 1].subColSpan &&
				(itemParts[i].start == itemParts[i + 1].end || itemParts[i].end == itemParts[i + 1].start)
			) {
				const newItem = {
					...itemParts[i],
					start: itemParts[i].start,
					startTimeMoment: itemParts[i].startTimeMoment,
					end: itemParts[i + 1].end,
					endTimeMoment: itemParts[i + 1].endTimeMoment,
				};
				itemParts[i] = newItem;
				itemParts.splice(i + 1, 1);
			}
		}
	}
	return itemParts;
}

export const prepareChildrenObj = (
	childrenObj: { [key: string]: IServiceEditForm['children'][string][number][1] },
	searchResults: IServicesList[],
) => {
	for (const service of searchResults) {
		for (const btchild of service.childrenBookingTemplates || []) {
			if (!(btchild.variantParentId in childrenObj)) {
				childrenObj[btchild.variantParentId] = [];
			}
			const existingIds = childrenObj[btchild.variantParentId].map((b) => b.id);
			if (!existingIds.includes(btchild.id)) {
				childrenObj[btchild.variantParentId].push({ ...btchild, active: true, readOnly: true });
			}
		}
	}
};

export function stateToEditFormProps(state: IStore, _variantParentId?: string, serviceGroupId?: string) {
	const shopCard = state.shopcard.shopcard;
	const offices = shopCard?.offices || [];
	const officeIds = offices.map((o) => o.id);
	const selectedServiceGroup = serviceGroupId
		? state.serviceGroups?.serviceGroups.find((x) => x.id === serviceGroupId)
		: '';
	const bookingtemplates = state.shopBookingTemplates;
	let editedServices: IServicesList[] = [];
	let serviceGroupValue = serviceGroupId || '';
	let serviceNameValue = '';
	const allServices = bookingtemplates?.services || [];
	if (allServices?.length && serviceGroupId && selectedServiceGroup) {
		// Targeted service subservices
		const targetedSubServices = selectedServiceGroup
			? allServices.filter((x) => x.serviceGroupId === selectedServiceGroup.id)
			: [];
		editedServices = targetedSubServices;

		// has subservices
		if (targetedSubServices.length && selectedServiceGroup) {
			serviceGroupValue = state.serviceGroups?.serviceGroups.find((x) => x.id === selectedServiceGroup.parentId).id;
			serviceNameValue = selectedServiceGroup.value;
		}
	} else {
		serviceGroupValue = serviceGroupId || (editedServices?.length && editedServices[0].serviceGroupId);
		serviceNameValue = '';
	}
	const performers: IServiceEditForm['performers'] = {};
	const subServices: IServiceEditForm['subServices'] = {};
	const pricesAndTimeline: IServiceEditForm['pricesAndTimeline'] = {};
	const serviceSettings: IServiceEditForm['serviceSettings'] = {
		[c.BASE_SERVICE]: {
			bookability: 1,
			type: 'standalone',
		},
	};

	const children: IServiceEditForm['children'] = {};
	if (editedServices.length) {
		// service has no subservices
		const childrenObj: { [key: string]: IServiceEditForm['children'][string][number][1] } = {};
		if (!editedServices.some((x) => editedServices[0].variantParentId !== x.variantParentId)) {
			pricesAndTimeline.currentService = c.BASE_SERVICE;

			if (!Object.keys(pricesAndTimeline).includes('serviceVariations')) {
				pricesAndTimeline.serviceVariations = {};
			}
			if (!Object.keys(pricesAndTimeline?.serviceVariations).includes(c.BASE_SERVICE)) {
				pricesAndTimeline.serviceVariations = {
					[c.BASE_SERVICE]: {},
				};
			}
			editedServices.forEach((x) => {
				pricesAndTimeline.serviceVariations[c.BASE_SERVICE][x.id] = {
					price: String(x.price),
					duration: String(x.duration / 60),
					performers: bookingtemplates.searchResults
						?.find((btsr) => btsr.id === x.id)
						?.resources?.reduce((obj, cur) => ({ ...obj, [(cur as IOfficeMember).id]: true }), {}),
				};
			});

			const serviceSample = editedServices[0];
			serviceSettings[c.BASE_SERVICE] = {
				bookability: serviceSample.published ? 1 : 0,
				type: bookingtemplates.searchResults?.find((as) => as.id === serviceSample.id)?.childrenBookingTemplates?.length
					? 'bundle'
					: 'standalone',
			};
			prepareChildrenObj(childrenObj, bookingtemplates.searchResults);
			children[c.BASE_SERVICE] = Object.entries(childrenObj);
			// service has subservices
		} else {
			const editedServicesWODuplicates: IServicesList[] = [];
			editedServices.forEach((x) => {
				if (!editedServicesWODuplicates.some((y) => y.name === x.name && y.serviceGroupId === x.serviceGroupId)) {
					editedServicesWODuplicates.push(x);
				}
			});
			editedServicesWODuplicates.forEach((x, index) => {
				subServices[`field-${index + 1}`] = {
					name: x.name,
					id: x.variantParentId,
				};
				serviceSettings[x.variantParentId] = {
					bookability: x.published ? 1 : 0,
					type: 'standalone',
				};
			});
		}
	}

	const serviceCategory = readURLSearchParams(c.SERVICE_CATEGORY)
		? Number(readURLSearchParams(c.SERVICE_CATEGORY))
		: editedServices.length
		  ? editedServices[0].categoryKey
		  : null;
	const serviceGroup = serviceGroupValue;
	const serviceName = serviceNameValue;
	const serviceDescription = editedServices.length ? editedServices[0].description : '';
	const defaultPubished = shopCard?.status === ShopStatus.Approved;
	const published: boolean = editedServices.length
		? editedServices.reduce((acc, val) => acc && val.published, true)
		: defaultPubished;

	const initialValues: Partial<IServiceEditForm> = {
		info: {
			serviceCategory,
			serviceGroup,
			serviceName,
			serviceDescription,
		},
		subServices,
		pricesAndTimeline,
		serviceSettings,
		performers,
		published,
		children,
		formIsDirty: false,
	};

	let currentFormValues: IServiceEditForm = {} as any;
	if (state.form[c.FORM_NAME_ADD_SERVICE] && state.form[c.FORM_NAME_ADD_SERVICE].values) {
		currentFormValues = state.form[c.FORM_NAME_ADD_SERVICE].values;
	}
	const serviceGroups = (state.serviceGroups.serviceGroups || []).filter((sg) => officeIds.includes(sg.officeId));
	return {
		shopCard,
		editedServices,
		currentFormValues,
		bookingtemplates,
		initialValues,
		serviceCategories: state.domainValues.serviceCategories,
		serviceGroups,
		isFetching:
			state.shopcard.isFetching ||
			state.shopBookingTemplates.isFetching ||
			state.domainValues.isFetchingServiceCategories ||
			state.serviceGroups.isFetching,
	};
}

export function formatMinServiceDuration(
	services: Array<Pick<IServicesList, 'duration'>>,
	t: (s: string) => string,
): string {
	if (!services.length) {
		return '';
	}
	if (services.length === 1) {
		return services[0].duration ? formatDuration(services[0].duration) : '';
	}
	const minDuration = services.reduce(
		(acc, val) => (isNaN(val.duration) || val.duration >= acc ? acc : val.duration),
		Infinity,
	);
	if (minDuration === Infinity || minDuration === 0) {
		return '';
	}
	return t('BSPortal_Common_From').toLowerCase() + ' ' + formatDuration(minDuration);
}

export function formatMinServicePrice(
	services: Array<Pick<IServicesList, 'price'>>,
	t: (s: string) => string,
	currencyKey?: number,
): string {
	if (!services.length) {
		return '';
	}
	if (services.length === 1) {
		return formatCurrency(currencyKey, services[0].price || null);
	}
	const minPrice = services.reduce((acc, val) => (isNaN(val.price) || val.price >= acc ? acc : val.price), Infinity);
	if (minPrice === Infinity || !minPrice || minPrice < 0) {
		return '';
	}
	return t('BSPortal_Common_From').toLowerCase() + ' ' + formatCurrency(currencyKey, minPrice);
}

export const getChildServiceGroups = (
	groupId: IServiceGroup['id'],
	groups: IServiceGroup[],
	bookableOnly?: IServicesList[][],
): IServiceGroup[] =>
	groups.filter((sg) => sg.parentId === groupId && (!bookableOnly || isBookable(groupId, groups, bookableOnly)));

export const getServicesDeep = (
	groupId: IServiceGroup['id'],
	groups: IServiceGroup[],
	groupedServices: IServicesList[][],
): IServicesList[][] => {
	let ret = groupedServices.filter((s) => s.length && s[0].serviceGroupId === groupId);
	for (const sg of getChildServiceGroups(groupId, groups)) {
		ret = [...ret, ...getServicesDeep(sg.id, groups, groupedServices)];
	}
	return ret;
};

export const getAllBookableChildren = (
	groupId: IServiceGroup['id'],
	groups: IServiceGroup[],
	groupedServices: IServicesList[][],
): Array<IServicesList[] | IServiceGroup> => [
	...getChildServiceGroups(groupId, groups, groupedServices),
	...groupedServices.filter((s) => s.length && s[0].serviceGroupId === groupId),
];

export const isBookable = (
	groupId: IServiceGroup['id'],
	groups: IServiceGroup[],
	groupedServices: IServicesList[][],
): boolean => getServicesDeep(groupId, groups, groupedServices).length > 0;

export const generateKey = (i: IServiceGroup | IServicesList[]): string | number =>
	Array.isArray(i) ? 's' + (i[0]?.variantParentId || Math.random()) : i.id;
export const generateId = (i: IServiceGroup | IServicesList[]): string => 'service-' + generateKey(i);

export function serviceTemplateToShopServiceTree(
	serviceTemplate: IShopServiceTemplate,
	maxLevel: number = 3,
): IShopServiceTreeItem[] {
	const applyOrderDeep = (item: IShopServiceTreeItem): void => {
		if (!item.childrenTemplateItems?.length) {
			return;
		}
		item.childrenTemplateItems.forEach(applyOrderDeep);
		item.childrenTemplateItems.sort((a, b) => (a.order < b.order ? -1 : 1));
	};

	const addToItemsListDeep = (
		itemsList: IShopServiceTreeItem[],
		item: IShopServiceTemplateItem,
		level: 0 | 1 | 2 | 3,
	) => {
		const idKey: keyof IShopServiceTemplateItem =
			level === 3 ? 'id' : level === 2 ? 'variantParentId' : level ? 'serviceGroupId' : 'parentServiceGroupId';
		const nameKey: keyof IShopServiceTemplateItem =
			level >= 2 ? 'name' : level ? 'serviceGroupName' : 'parentServiceGroupName';
		const orderKey: keyof IShopServiceTemplateItem = level ? 'serviceGroupOrder' : 'parentServiceGroupOrder';
		let index = itemsList.findIndex((i) => i.id === item[idKey]);
		if (index < 0) {
			index = itemsList.length;
			itemsList.push({
				...item,
				childrenTemplateItems: [],
				name: item[nameKey],
				order: item[orderKey],
				id: item[idKey],
				serviceCategoryId: item.serviceCategoryId,
				type: level === 3 ? 'variant' : level === 2 ? 'service' : 'serviceGroup',
				level,
			});
		}
		if (level >= maxLevel) {
			return;
		}
		addToItemsListDeep(itemsList[index].childrenTemplateItems, item, (level + 1) as 1 | 2 | 3);
	};

	const usedServiceCategoriesIds = uniq((serviceTemplate?.templateItems || [])?.map((ti) => ti.serviceCategoryId));

	return usedServiceCategoriesIds
		.map((scId): IShopServiceTreeItem => {
			const items = (serviceTemplate.templateItems || []).filter((i) => i.serviceCategoryId === scId);
			if (!items.length) {
				return null;
			}
			const rootGroups: IShopServiceTreeItem[] = [];
			for (const item of items) {
				addToItemsListDeep(rootGroups, item, 0);
			}
			const result: IShopServiceTreeItem = {
				id: String(scId),
				name: i18n.t('DV_ServiceCategories_' + scId),
				childrenTemplateItems: rootGroups,
				type: 'serviceCategory',
			};
			applyOrderDeep(result);
			return result;
		})
		.filter(Boolean);
}

export function structureToShopServiceTree(
	serviceCategories: IServiceCategoryDomainValue[],
	serviceGroups: IServiceGroup[],
	services: IServicesList[],
): IShopServiceTreeItem[] {
	const groupedServices = groupServices(services);

	const getChildren = (groupId: IServiceGroup['id']): IShopServiceTreeItem[] =>
		getAllBookableChildren(groupId, serviceGroups, groupedServices).map(childToItem);
	const childToItem = (child: IServicesList[] | IServiceGroup): IShopServiceTreeItem => {
		if (Array.isArray(child)) {
			const first = child[0];
			return {
				id: first.variantParentId,
				name: first.name,
				type: 'service',
			};
		} else {
			return {
				id: child.id,
				name: child.value,
				childrenTemplateItems: getChildren(child.id),
				type: 'serviceGroup',
			};
		}
	};
	return serviceCategories
		.map((sc): IShopServiceTreeItem => {
			const rootGroups = serviceGroups
				.filter(
					(sg) => !sg.parentId && sg.serviceCategoryId === sc.key && isBookable(sg.id, serviceGroups, groupedServices),
				)
				.map(childToItem);
			if (!rootGroups.length) {
				return null;
			}
			return {
				id: String(sc.key),
				name: i18n.t('DV_ServiceCategories_' + sc.key),
				childrenTemplateItems: rootGroups,
				type: 'serviceCategory',
			};
		})
		.filter(Boolean);
}
